import type { HydraCollection } from "@verbleif/lib/src/composables/Api/Type/hydra.interface";
import type { AxiosRequestConfig } from "axios";
import { api } from "../Api/api";
import { fetchQueryOnce } from "./fetchQueryOnce";

type ResourceQueryParams = URLSearchParams | Record<string, string | number | boolean | undefined | null>;

export function toSearchParams(params?: ResourceQueryParams): URLSearchParams {
  if (!params) {
    return new URLSearchParams();
  }
  if (params instanceof URLSearchParams) {
    return new URLSearchParams(params);
  }
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }
    search.set(key, String(value));
  }
  return search;
}

function headersKeyPart(headers: AxiosRequestConfig["headers"]): string[][] {
  if (!headers || typeof headers !== "object") {
    return [];
  }
  return Object.entries(headers as Record<string, unknown>)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([name, value]) => [name.toLowerCase(), String(value)])
    .sort(([left], [right]) => left.localeCompare(right));
}

function requestParams(params?: ResourceQueryParams): ResourceQueryParams | undefined {
  if (!params) {
    return undefined;
  }
  if (params instanceof URLSearchParams) {
    return new URLSearchParams(params);
  }
  return params;
}

export function resourceQueryOptions<T>({
  queryKey,
  url,
  params,
  config,
}: {
  queryKey: readonly unknown[]
  url: string
  params?: ResourceQueryParams
  config?: AxiosRequestConfig
}): {
  queryKey: readonly unknown[]
  queryFn: (context: { signal?: AbortSignal }) => Promise<T>
} {
  return {
    queryKey,
    queryFn: async ({ signal }) => {
      const { data } = await api.get<T>(url, {
        ...config,
        params: requestParams(params) ?? config?.params,
        signal,
      });
      return data;
    },
  };
}

export function fetchResource<T>(
  url: string,
  {
    params,
    gcTime,
    config,
  }: {
    params?: ResourceQueryParams
    gcTime?: number
    config?: AxiosRequestConfig
  } = {},
): Promise<T> {
  const effectiveParams: ResourceQueryParams | undefined = requestParams(params) ?? config?.params;
  const search = toSearchParams(effectiveParams);
  search.sort();
  return fetchQueryOnce({
    queryKey: ["resource", url, search.toString(), config?.responseType ?? null, headersKeyPart(config?.headers)],
    staleTime: 0,
    ...(gcTime !== undefined ? { gcTime } : {}),
    queryFn: async ({ signal }) => {
      const { data } = await api.get<T>(url, {
        ...config,
        params: effectiveParams,
        signal,
      });
      return data;
    },
  });
}

export function fetchCollection<T>(
  url: string,
  options: {
    params?: ResourceQueryParams
    gcTime?: number
    config?: AxiosRequestConfig
  } = {},
): Promise<HydraCollection<T>> {
  return fetchResource<HydraCollection<T>>(url, options);
}
